import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import workflowService from '../../services/workflow.service';
import callbacksService from '../../services/callbacks.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { resolveFields, type FormFieldDef } from '../../lib/workflowForm';
import type { CaseDetailFull } from '../../types/workflow';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import DynamicForm from '../../components/workflow/DynamicForm';
import PhoneVerificationSection from '../../components/security/PhoneVerificationSection';
import OnboardingSubStepper, { type SubSection } from '../../components/workflow/OnboardingSubStepper';
import KycInfoRequestBanner from '../../components/client/KycInfoRequestBanner';
import KycStatementsPanel from '../../components/client/KycStatementsPanel';
import { colors } from '../../styles/theme';
import { useRegisterWidget } from '../../components/layout/RealtimeProvider';
import { isInReview } from '../../lib/onboardingStatus';

const WORKFLOW_SLUG = 'onboarding_retail';

/** Client-editable states in `onboarding_retail`. Anything else is a
 *  staff-side state and the wizard shows a status panel instead of a
 *  form. Order matches workflow display_order so the stepper reads
 *  left-to-right. */
const CLIENT_EDITABLE_STATES: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'pending_profile',      label: 'Personal data' },
  { slug: 'pending_pep_aml',      label: 'PEP / AML' },
  { slug: 'pending_documents',    label: 'Documents' },
  { slug: 'information_required', label: 'Resubmission' },
];

/** Full onboarding journey shown in the macro stepper — includes
 *  staff-side phases so the client sees the complete picture. */
const MACRO_PHASES = [
  { slug: 'pending_profile',   label: 'Personal data' },
  { slug: 'pending_pep_aml',   label: 'PEP / AML' },
  { slug: 'pending_documents', label: 'Documents' },
  { slug: 'under_review',      label: 'Under review' },
  { slug: 'approved',          label: 'Approved' },
] as const;

/** Map any workflow slug to the index of the "current" macro phase. */
function macroPhaseIndex(slug: string): number {
  const directIdx = MACRO_PHASES.findIndex((p) => p.slug === slug);
  if (directIdx !== -1) return directIdx;
  // Aliases
  if (slug === 'registered') return -1; // not started yet — all phases pending
  if (slug === 'information_required') return 2; // re-work on Documents step
  if (['pending_rejection', 'pending_rejection_review', 'restricted'].includes(slug)) return 3;
  if (['rejected', 'archived'].includes(slug)) return 3; // stopped at review
  return 3; // anything unknown in "review" territory
}

/** Sub-sections within `pending_profile`. Keys match the `section`
 *  column values in workflow.form_fields as seeded in schema.sql. */
const PROFILE_SECTIONS: ReadonlyArray<SubSection> = [
  { key: 'Identity',  label: 'Personal Info' },
  { key: 'Legal',     label: 'Legal / Tax IDs' },
  { key: 'Address',   label: 'Address' },
  { key: 'Financial', label: 'Financial Profile' },
];

/** Find the index of the first section that still has required
 *  fields without a valid value. If all sections are complete,
 *  returns the last index so the user lands on the final submit step. */
function deriveInitialSectionIndex(
  fields: FormFieldDef[],
  data: Record<string, unknown>,
): number {
  for (let i = 0; i < PROFILE_SECTIONS.length; i++) {
    const key = PROFILE_SECTIONS[i].key;
    const sectionFields = fields.filter((f) => f.section === key);
    if (sectionFields.length === 0) continue;
    const states = resolveFields(sectionFields, data);
    const done = states.every((s) => !s.visible || !s.enabled || s.error === null);
    if (!done) return i;
  }
  return PROFILE_SECTIONS.length - 1;
}

function resolveSubmitLabel(currentSlug: string, sectionIndex: number): string {
  if (currentSlug === 'pending_profile') {
    return sectionIndex < PROFILE_SECTIONS.length - 1 ? 'Continue →' : 'Submit for review';
  }
  if (currentSlug === 'pending_documents') return 'Submit for review';
  if (currentSlug === 'information_required') return 'Resubmit';
  return 'Continue';
}


const TERMINAL_TONE: Record<string, { tone: BadgeTone; title: string; body: string }> = {
  registered: {
    tone: 'info',
    title: 'Setting up your case…',
    body: "We're preparing your onboarding workflow. This page will refresh in a moment.",
  },
  under_review: {
    tone: 'info',
    title: 'In review',
    body: 'Our team is reviewing your submission. Nothing is needed from you right now — you can close this page and we will email you with the outcome.',
  },
  // pending_decision (a clean hand-off to a supervisor) and
  // pending_rejection (an agent proposed rejection, awaiting a manager's
  // sign-off) are internal SOD sub-states. The client only ever sees a
  // neutral "final review" message — we never reveal the proposal, the
  // reviewer, or that a rejection is on the table. Both carry the same
  // "no action needed, we'll email you" expectation so the wait never
  // reads as a dead end.
  pending_decision: {
    tone: 'info',
    title: 'In final review',
    body: 'Your application is with a supervisor for the final decision. Nothing is needed from you right now — we will email you with the outcome.',
  },
  pending_rejection: {
    tone: 'info',
    title: 'In final review',
    body: 'Your application is going through a final compliance review. Nothing is needed from you right now — we will email you with the outcome, and your Account Executive will reach out if anything is required.',
  },
  approved: {
    tone: 'success',
    title: 'Approved',
    body: 'Your onboarding is complete. You can now access your summary.',
  },
  rejected: {
    tone: 'danger',
    title: 'Onboarding rejected',
    body: 'Your case was not approved. Please contact your Account Executive for details.',
  },
  restricted: {
    tone: 'warning',
    title: 'Pending compliance review',
    body: 'Your case is on hold for an additional compliance check. We will reach out shortly.',
  },
  archived: {
    tone: 'neutral',
    title: 'Case archived',
    body: 'This case has been archived for legal retention. Contact support if you need a new application.',
  },
};

// The states in which the signed statements are shown: the last step before the submit.
const STATEMENT_STATES: ReadonlySet<string> = new Set(['pending_documents', 'information_required']);

const Onboarding = (): JSX.Element => {
  const navigate = useNavigate();
  // Optional deep link — the compliance banner routes to a specific remediation
  // case via /client/onboarding/:caseId. When present we load THAT case instead
  // of resume-or-start so the client lands on exactly the case to refresh.
  const { caseId: routeCaseId } = useParams<{ caseId?: string }>();
  const [caseDetail, setCaseDetail] = useState<CaseDetailFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const autoStartFired = useRef(false);

  // Sub-section index within `pending_profile`. Initialized once when the
  // case loads (A7: derive from saved data completeness). Null while the
  // case hasn't loaded yet.
  const [sectionIndex, setSectionIndex] = useState<number>(0);
  // Tracks whether we have set the initial sectionIndex for this case load.
  const sectionInitialized = useRef(false);

  const refresh = useCallback(async (caseId: string) => {
    const detail = await workflowService.getCaseFull(caseId);
    setCaseDetail(detail);
    return detail;
  }, []);

  // SSE push: when an agent transitions the case, refresh immediately
  // so the client sees the new state without polling. Registers via
  // the session-wide RealtimeProvider (mounted in MainLayout); the
  // `appointment.imminent` reminder is handled by LayoutRealtimeBus
  // in the same provider.
  const refetchOnboardingCase = useCallback(() => {
    if (caseDetail) refresh(caseDetail.case_id);
  }, [caseDetail, refresh]);
  useRegisterWidget('onboarding-case', refetchOnboardingCase);

  useEffect(() => {
    let cancelled = false;

    // First-mount only: a freshly-resumed case sitting in `registered` with a
    // START event available is auto-advanced once. Extracted from the load
    // flow below to keep each function's branching shallow.
    const maybeAutoStart = async (caseId: string, detail: CaseDetailFull): Promise<void> => {
      if (
        detail.current_state_slug !== 'registered' ||
        !detail.available_events.includes('START') ||
        autoStartFired.current
      ) {
        return;
      }
      autoStartFired.current = true;
      try {
        await workflowService.applyTransition(caseId, { event_trigger: 'START' });
        if (!cancelled) await refresh(caseId);
      } catch (e: unknown) {
        // Was silent, and the guard above is one-shot, so a failure left the
        // client on "Setting up your case… this page will refresh in a moment"
        // indefinitely, on the first screen a new investor sees. Release the
        // guard so a reload retries, and say what happened.
        autoStartFired.current = false;
        if (!cancelled) {
          setError(getApiErrorMessage(
            e,
            'We could not start your application. Please reload the page to try again.',
          ));
        }
      }
    };

    const load = async (): Promise<void> => {
      let caseId: string | null = null;
      try {
        if (routeCaseId) {
          // Deep-linked case (e.g. a compliance remediation case): load it
          // directly. It already sits in a client-editable state, so there's no
          // resume-or-start and no auto-START to fire.
          caseId = routeCaseId;
          await refresh(caseId);
          return;
        }
        const resumed = await workflowService.resumeOrStartOnboarding(WORKFLOW_SLUG);
        caseId = resumed.case_id;
        const detail = await refresh(caseId);
        if (cancelled) return;
        await maybeAutoStart(caseId, detail);
      } catch (e: unknown) {
        // A `caseId === null` guard used to discard any failure that happened
        // after the case id was known, so a failed detail load rendered an empty
        // page. Every failure here now reaches the screen.
        if (!cancelled) {
          setError(getApiErrorMessage(e, 'Could not load your onboarding case.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load().catch(() => { /* load() handles its own errors */ });
    return () => { cancelled = true; };
  }, [refresh, routeCaseId]);

  // A7: Once the case is available for the first time, derive which
  // pending_profile sub-section to start from based on saved progress.
  useEffect(() => {
    if (!caseDetail || sectionInitialized.current) return;
    if (caseDetail.current_state_slug === 'pending_profile') {
      setSectionIndex(deriveInitialSectionIndex(caseDetail.fields, caseDetail.data));
    }
    sectionInitialized.current = true;
  }, [caseDetail]);

  // Fields for the current pending_profile sub-section only.
  // Other states get all their fields unchanged.
  const activeFields = useMemo(() => {
    if (!caseDetail) return [];
    if (caseDetail.current_state_slug !== 'pending_profile') return caseDetail.fields;
    const key = PROFILE_SECTIONS[sectionIndex].key;
    return caseDetail.fields.filter((f) => f.section === key);
  }, [caseDetail, sectionIndex]);

  // Does the section on screen capture the mobile? Then offer to prove it here.
  // Prefilled from the SAVED value: the form owns its own draft state.
  const capturesMobile = activeFields.some((f) => f.control_type === 'phone');
  const savedMobile = typeof caseDetail?.data?.phone === 'string' ? caseDetail.data.phone : '';

  const handleSave = async (
    payload: Array<{ field_key: string; value: unknown }>,
  ): Promise<void> => {
    if (!caseDetail || payload.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { written } = await workflowService.saveCaseData(caseDetail.case_id, { items: payload });
      setFlash(`Saved ${written} ${written === 1 ? 'field' : 'fields'}.`);
      globalThis.setTimeout(() => setFlash(null), 4000);
      await refresh(caseDetail.case_id);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not save your progress.'));
    } finally {
      setBusy(false);
    }
  };

  // A5 / A6: Advance to the next sub-section, auto-saving the current one first.
  const handleContinueSection = async (
    payload: Array<{ field_key: string; value: unknown }>,
  ): Promise<void> => {
    if (!caseDetail) return;
    setBusy(true);
    setError(null);
    try {
      if (payload.length > 0) {
        await workflowService.saveCaseData(caseDetail.case_id, { items: payload });
      }
      await refresh(caseDetail.case_id);
      setSectionIndex((prev) => prev + 1);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not save your progress.'));
    } finally {
      setBusy(false);
    }
  };

  const handleBack = (): void => {
    setSectionIndex((prev) => Math.max(0, prev - 1));
  };

  const handleSubmit = async (
    payload: Array<{ field_key: string; value: unknown }>,
  ): Promise<void> => {
    if (!caseDetail) return;
    const event = caseDetail.available_events[0];
    if (!event) {
      setError('There is nothing to send right now. Reload the page and try again.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (payload.length > 0) {
        await workflowService.saveCaseData(caseDetail.case_id, { items: payload });
      }
      await workflowService.applyTransition(caseDetail.case_id, { event_trigger: event });
      await refresh(caseDetail.case_id);
      // In the client's words: the name of an internal state means nothing to them.
      setFlash('Your application was sent for review.');
      globalThis.setTimeout(() => setFlash(null), 4000);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not submit your application.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Onboarding" />
        <div className="rounded-2xl p-6" style={{ background: colors.surface }}>
          <p style={{ color: colors.muted }}>Loading your application…</p>
        </div>
      </>
    );
  }

  if (error && !caseDetail) {
    return (
      <>
        <PageHeader title="Onboarding" />
        <div
          role="alert"
          className="rounded-2xl p-6"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => navigate('/client/dashboard')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold"
              style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!caseDetail) return <></>;

  const isEditable = CLIENT_EDITABLE_STATES.some(
    (s) => s.slug === caseDetail.current_state_slug,
  );

  const isPendingProfile = caseDetail.current_state_slug === 'pending_profile';
  const isLastSection = sectionIndex === PROFILE_SECTIONS.length - 1;

  // For pending_profile: "Continue →" on all but the last section;
  // "Submit for review" on the last. Other states keep their own label.
  const submitLabel = resolveSubmitLabel(caseDetail.current_state_slug, sectionIndex);

  // For pending_profile: non-last sections advance the sub-section
  // instead of firing a workflow transition.
  const onSubmit = isPendingProfile && !isLastSection
    ? handleContinueSection
    : handleSubmit;

  return (
    <>
      <PageHeader
        title="Onboarding"
        subtitle="Complete each step to make your first contribution. We save as you go — you can come back later."
      />

      {flash && (
        <output
          className="block mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.successText }}
        >
          {flash}
        </output>
      )}

      {error && (
        <div
          role="alert"
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {caseDetail.current_state_slug === 'information_required' &&
       caseDetail.latest_client_message && (
        <KycInfoRequestBanner message={caseDetail.latest_client_message} />
      )}

      <MacroStepper currentSlug={caseDetail.current_state_slug} />

      {/* Body */}
      {isEditable ? (
        <div
          className="rounded-2xl p-6"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          {/* Sub-stepper: only shown while the client is filling in their profile. */}
          {isPendingProfile && (
            <OnboardingSubStepper
              sections={PROFILE_SECTIONS}
              currentIndex={sectionIndex}
            />
          )}

          {/* The last thing before submitting: the tax residence declaration and the declarations,
              printed from the answers above and signed once (owner, 2026-10-24). The server
              refuses the submit until both are current. */}
          {STATEMENT_STATES.has(caseDetail.current_state_slug) && (
            <KycStatementsPanel caseId={caseDetail.case_id} />
          )}

          <DynamicForm
            key={isPendingProfile ? `profile-${sectionIndex}` : caseDetail.current_state_slug}
            fields={activeFields}
            initialData={caseDetail.data}
            busy={busy}
            onSave={handleSave}
            onSubmit={onSubmit}
            onUploadFile={(fieldKey, file, options) =>
              workflowService.uploadKycDocument(caseDetail.case_id, fieldKey, file, options)
            }
            documentExpiries={Object.fromEntries(
              caseDetail.documents.map((d) => [
                d.document_id,
                { expires_at: d.expires_at, expiry_granularity: d.expiry_granularity },
              ]),
            )}
            onSaveDocumentExpiry={async (documentId, body) => {
              const res = await workflowService.updateDocumentExpiry(
                caseDetail.case_id, documentId, body,
              );
              return {
                expires_at: res.expires_at,
                expiry_granularity: res.expiry_granularity,
              };
            }}
            submitLabel={submitLabel}
            fieldReviews={caseDetail.field_reviews}
            onBack={isPendingProfile && sectionIndex > 0 ? handleBack : undefined}
          />

          {/* The mobile is proven HERE, once, where it is first typed — every
              later flow then simply uses it. Shown beside the form section that
              captures it. Never blocks this page: the server decides at SUBMIT
              whether a proven number is required (409 phone_not_verified /
              phone_mismatch), and says so in words if it is. */}
          {capturesMobile && (
            <div
              className="mt-6 p-4 rounded-xl"
              style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}
            >
              <h3 className="text-sm font-semibold" style={{ color: colors.ink }}>
                Confirm your mobile number
              </h3>
              <p className="text-sm mt-1 mb-1" style={{ color: colors.inkSoft }}>
                We send a one-time code to the number above, so we know we can reach you on it.
                Use the same number you entered in the form.
              </p>
              <PhoneVerificationSection embedded initialPhone={savedMobile} />
            </div>
          )}
        </div>
      ) : (
        <StatusPanel
          currentSlug={caseDetail.current_state_slug}
          rejectionMessage={
            caseDetail.current_state_slug === 'rejected'
              ? caseDetail.latest_client_message?.summary ?? null
              : null
          }
        />
      )}
    </>
  );
};

const REVIEW_STEPS = [
  { label: 'Identity verification', detail: 'We confirm your ID documents and personal data match.' },
  { label: 'KYC / AML screening', detail: 'Your profile is checked against regulatory watchlists.' },
  { label: 'Compliance approval', detail: 'Our team reviews and approves your application.' },
  { label: 'Account activation', detail: 'Your summary becomes accessible and your Account Executive contacts you.' },
];

// ─── GitLab #49 follow-up: under-review advisor CTA ────────────────
// Mirrors the OnboardingProgressCard UnderReviewNextSteps button so
// the dashboard widget and the wizard's under-review status panel
// share one behaviour: a single click lodges a callback request with
// the client's assigned agent, idempotent server-side.

type CallbackUiState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'requested'; advisorName: string | null; alreadyExisted: boolean }
  | { kind: 'error'; message: string };

const UnderReviewAdvisorButton = (): JSX.Element => {
  const [state, setState] = useState<CallbackUiState>({ kind: 'idle' });

  async function handleMessage(): Promise<void> {
    if (state.kind === 'submitting' || state.kind === 'requested') return;
    setState({ kind: 'submitting' });
    try {
      const res = await callbacksService.requestCallback({
        context_note: 'From under-review onboarding wizard.',
      });
      const advisorName =
        res.advisor_first_name || res.advisor_last_name
          ? `${res.advisor_first_name ?? ''} ${res.advisor_last_name ?? ''}`.trim()
          : null;
      setState({
        kind: 'requested',
        advisorName,
        alreadyExisted: res.already_existed,
      });
    } catch (err: unknown) {
      setState({
        kind: 'error',
        message: getApiErrorMessage(
          err,
          "Couldn't reach your Account Executive right now. Please try again.",
        ),
      });
    }
  }

  const isSubmitting = state.kind === 'submitting';
  const isRequested = state.kind === 'requested';

  return (
    <div className="space-y-2">
      <div
        className="flex items-center justify-between gap-4 rounded-xl px-4 py-3 flex-wrap"
        style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
      >
        <p className="text-sm" style={{ color: colors.inkSoft }}>
          Have questions while you wait?
        </p>
        <button
          type="button"
          onClick={handleMessage}
          disabled={isSubmitting || isRequested}
          className="flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          style={{
            background: isRequested ? colors.successBg : colors.surface,
            color: isRequested ? colors.success : colors.gold,
            border: `1px solid ${isRequested ? colors.successBorder : colors.gold}`,
            cursor: isSubmitting || isRequested ? 'default' : 'pointer',
          }}
        >
          {isSubmitting && 'Requesting…'}
          {state.kind === 'idle' && 'Message your Account Executive →'}
          {isRequested && (
            <>
              ✓{' '}
              {state.alreadyExisted
                ? 'Your Account Executive already has this request'
                : 'Your Account Executive has been notified'}
              {state.advisorName && !state.alreadyExisted
                ? ` — ${state.advisorName} will reach out`
                : ''}
            </>
          )}
          {state.kind === 'error' && 'Try again'}
        </button>
      </div>
      {state.kind === 'error' && (
        <p className="text-xs" role="alert" style={{ color: colors.danger }}>
          {state.message}
        </p>
      )}
    </div>
  );
};


/** `rejectionMessage`: what the reviewer wrote to the client when the case was rejected. It is
 *  read here, never in the e-mail (owner, 2026-10-20). */
const StatusPanel = ({
  currentSlug,
  rejectionMessage = null,
}: {
  currentSlug: string;
  rejectionMessage?: string | null;
}): JSX.Element => {
  // Never leak a raw state slug to the client: unknown states fall back to
  // a friendly, reassuring "no action needed" panel rather than printing
  // e.g. "pending_decision" as the title.
  const meta = TERMINAL_TONE[currentSlug] ?? {
    tone: 'neutral' as BadgeTone,
    title: 'Application in progress',
    body: 'Your application is being processed. Nothing is needed from you right now — we will email you if anything changes.',
  };

  // Show the "what happens next" timeline + advisor CTA for every state
  // where the case is waiting on staff, so none reads as a silent dead end.
  const isUnderReview = isInReview(currentSlug);

  return (
    <div
      className="rounded-2xl p-8"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div className={`mb-3 ${isUnderReview ? '' : 'text-center'}`}>
        <StatusBadge tone={meta.tone}>{meta.title}</StatusBadge>
      </div>
      <p
        className={`text-sm leading-relaxed ${isUnderReview ? '' : 'text-center max-w-md mx-auto'}`}
        style={{ color: colors.inkSoft }}
      >
        {meta.body}
      </p>

      {rejectionMessage && (
        <div
          className="mt-5 rounded-xl p-4 max-w-md mx-auto text-left"
          style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
        >
          <p className="text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            Our team&apos;s message
          </p>
          <p className="text-sm whitespace-pre-wrap" style={{ color: colors.ink }}>
            {rejectionMessage}
          </p>
        </div>
      )}

      {isUnderReview && (
        <div className="mt-6 space-y-5">
          {/* Review steps */}
          <ol className="space-y-3">
            {REVIEW_STEPS.map((step, i) => (
              <li key={step.label} className="flex gap-3 items-start text-sm">
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: colors.gold + '22', color: colors.gold, border: `1px solid ${colors.gold}` }}
                >
                  {i + 1}
                </span>
                <div>
                  <div className="font-semibold" style={{ color: colors.ink }}>{step.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: colors.muted }}>{step.detail}</div>
                </div>
              </li>
            ))}
          </ol>

          {/* Accordion */}
          <details
            className="rounded-xl overflow-hidden"
            style={{ border: `1px solid ${colors.border}` }}
          >
            <summary
              className="px-4 py-3 text-sm font-semibold cursor-pointer select-none"
              style={{ color: colors.ink, background: colors.canvas, listStyle: 'none' }}
            >
              What happens next?
            </summary>
            <div className="px-4 py-3 text-sm space-y-2" style={{ color: colors.inkSoft }}>
              <p>Once approved, you will receive a confirmation email and your account summary becomes active.</p>
              <p>If our team needs clarification, you will receive an email with specific items to address. The portal will reopen so you can resubmit directly.</p>
              <p>In rare cases requiring additional compliance steps, you may be contacted by your assigned Account Executive by phone.</p>
            </div>
          </details>

          {/* Advisor CTA — direct-to-assigned-agent callback request.
              Replaces the previous generic /contact link so the request
              lands in the assigned agent's work-queue (and an email)
              instead of the public contact form. */}
          <UnderReviewAdvisorButton />
        </div>
      )}
    </div>
  );
};

const MacroStepper = ({ currentSlug }: { currentSlug: string }): JSX.Element => {
  const activeIdx = macroPhaseIndex(currentSlug);
  const isRejected = ['rejected', 'archived'].includes(currentSlug);

  return (
    <div className="mb-6">
      <div className="flex items-center" style={{ gap: 0 }}>
        {MACRO_PHASES.map((phase, i) => {
          const state = macroPhaseState(i, activeIdx);
          const isLast = i === MACRO_PHASES.length - 1;
          const dotBg = macroDotColor(state, isRejected);
          const labelColor = macroLabelColor(state, isRejected);

          return (
            <div key={phase.slug} className="flex items-center flex-1 min-w-0">
              <div className="flex flex-col items-center" style={{ minWidth: 0, flex: '0 1 auto' }}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: dotBg, color: '#fff', flexShrink: 0 }}
                >
                  {state === 'done' ? '✓' : i + 1}
                </div>
                <span
                  className="text-xs mt-1 text-center leading-tight"
                  style={{ color: labelColor, fontWeight: state === 'current' ? 600 : 400 }}
                >
                  {phase.label}
                </span>
              </div>
              {!isLast && (
                <div
                  className="flex-1 h-px mx-1"
                  style={{ background: i < activeIdx ? colors.success : colors.border, marginBottom: 14 }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Onboarding;

type MacroPhaseState = 'done' | 'current' | 'pending';

function macroPhaseState(i: number, activeIdx: number): MacroPhaseState {
  if (i < activeIdx) return 'done';
  if (i === activeIdx) return 'current';
  return 'pending';
}

function macroDotColor(state: MacroPhaseState, isRejected: boolean): string {
  if (state === 'done') return colors.success;
  if (state === 'current') return isRejected ? colors.danger : colors.gold;
  return colors.borderSoft;
}

function macroLabelColor(state: MacroPhaseState, isRejected: boolean): string {
  if (state === 'done') return colors.success;
  if (state === 'current') return isRejected ? colors.danger : colors.ink;
  return colors.muted;
}
